import { GeminiClient } from "./base/GeminiClient";
import { PromptBuilders } from "./utils/PromptBuilders";
import { ResponseParsers } from "./utils/ResponseParsers";
import type {
  GenerateSchemaRequest,
  GenerateSchemaResponse,
} from "@/features/Projects";

export class SchemaService extends GeminiClient {
  async generateSchema(
    request: GenerateSchemaRequest
  ): Promise<GenerateSchemaResponse> {
    if (!request.projectDescription?.trim()) {
      throw new Error("Descrição do projeto é obrigatória");
    }

    if (!request.objectives || request.objectives.length === 0) {
      throw new Error("Objetivos do projeto são obrigatórios");
    }

    // Schema é sempre complexo, usar batch processing por padrão
    const shouldUseBatches = true;
    
    try {
      if (shouldUseBatches) {
        return await this.generateSchemaInBatches(request);
      } else {
        // Fallback para geração completa (mantido por compatibilidade)
        return await this.generateSchemaComplete(request);
      }
    } catch (error) {
      console.error("Erro ao gerar schema:", error);
      throw new Error("Falha ao processar schema com IA");
    }
  }

  private async generateSchemaComplete(
    request: GenerateSchemaRequest
  ): Promise<GenerateSchemaResponse> {
    const prompt = PromptBuilders.buildSchemaPrompt(request);
    const response = await this.callGeminiAPI(prompt);
    const result = ResponseParsers.parseSchemaResponse(response);
    return result;
  }

  private async generateSchemaInBatches(
    request: GenerateSchemaRequest
  ): Promise<GenerateSchemaResponse> {
    try {
      // Fase 1: Gerar modelos de dados
      const dataModels = await this.generateDataModels({
        projectDescription: request.projectDescription,
        objectives: request.objectives,
        technicalContext: request.technicalSolution?.description || "",
        storiesContext: JSON.stringify(request.stories),
        databasePreference: request.databasePreference || "postgresql",
      });

      // Pausa entre batches
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Fase 2: Gerar endpoints baseados nos modelos
      const apiEndpoints = await this.generateApiEndpoints({
        projectDescription: request.projectDescription,
        objectives: request.objectives,
        dataModels: dataModels,
        apiStyle: request.apiStyle || "rest",
        authRequired: request.authRequired || true,
        technicalSolution: request.technicalSolution,
        stories: request.stories,
        timeline: request.timeline,
      });

      // Consolidar resultados
      const currentTime = new Date().toISOString();
      
      const totalFields = dataModels.reduce(
        (total: number, model: any) => total + (model.fields?.length || 0),
        0
      );

      const schema = {
        dataModels: dataModels,
        apiEndpoints: apiEndpoints,
        apiGroups: this.generateApiGroups(apiEndpoints),
        documentation: this.generateDocumentation(dataModels, apiEndpoints, request),
        config: {
          apiVersion: "v1",
          baseUrl: `https://api.${request.projectDescription.toLowerCase().replace(/\s+/g, '-')}.com`,
          databaseType: (request.databasePreference as "postgresql" | "mysql" | "mongodb" | "sqlite") || "postgresql",
          authenticationMethod: request.authRequired ? "jwt" as const : "none" as const,
          corsEnabled: true,
          rateLimitEnabled: true,
          loggingEnabled: true,
          openApiInfo: {
            title: `${request.projectDescription} API`,
            description: request.projectDescription,
            version: "1.0.0",
          },
        },
        stats: {
          totalModels: dataModels.length,
          totalEndpoints: apiEndpoints.length,
          totalFields: totalFields,
          complexity: this.calculateComplexity(dataModels.length, apiEndpoints.length),
        },
        createdAt: currentTime,
        updatedAt: currentTime,
      };

      return { schema };

    } catch (error) {
      console.error("Erro durante geração em batches:", error);
      throw error;
    }
  }

  private generateApiGroups(endpoints: any[]): any[] {
    const groups: { [key: string]: any } = {};
    
    endpoints.forEach(endpoint => {
      const resource = endpoint.path.split('/')[1] || 'general';
      if (!groups[resource]) {
        groups[resource] = {
          id: resource,
          name: resource.charAt(0).toUpperCase() + resource.slice(1),
          description: `Operações relacionadas a ${resource}`,
          endpoints: [],
        };
      }
      groups[resource].endpoints.push(endpoint.id);
    });

    return Object.values(groups);
  }

  private generateDocumentation(dataModels: any[], endpoints: any[], request: GenerateSchemaRequest): any[] {
    return [
      {
        id: "overview",
        title: "Visão Geral da API",
        content: `API para ${request.projectDescription}. Inclui ${dataModels.length} modelos de dados e ${endpoints.length} endpoints.`,
        type: "markdown",
      },
      {
        id: "authentication",
        title: "Autenticação",
        content: request.authRequired 
          ? "Esta API utiliza JWT (JSON Web Tokens) para autenticação. Inclua o token no header Authorization: Bearer <token>."
          : "Esta API não requer autenticação.",
        type: "markdown",
      },
    ];
  }

  private calculateComplexity(modelCount: number, endpointCount: number): "low" | "medium" | "high" {
    const totalComplexity = modelCount + (endpointCount * 0.5);
    if (totalComplexity < 10) return "low";
    if (totalComplexity < 25) return "medium";
    return "high";
  }

  async generateDataModels(request: {
    projectDescription: string;
    objectives: string[];
    technicalContext: string;
    storiesContext: string;
    databasePreference: string;
  }): Promise<any[]> {
    if (!request.projectDescription?.trim()) {
      throw new Error("Descrição do projeto é obrigatória");
    }

    if (!request.objectives || request.objectives.length === 0) {
      throw new Error("Objetivos do projeto são obrigatórios");
    }

    // Para modelos de dados, usar estratégia mais conservadora
    // Primeiro tenta geração completa, se falhar, não usa batches (modelos podem ter dependências)
    try {
      const prompt = PromptBuilders.buildDataModelsPrompt(
        request.projectDescription,
        request.objectives,
        request.technicalContext,
        request.storiesContext,
        request.databasePreference
      );
      
      const response = await this.callGeminiAPI(prompt);
      
      this.logApiResponse(response, 'generateDataModels', {
        projectDescription: request.projectDescription,
        objectives: request.objectives,
        databasePreference: request.databasePreference
      });

      return ResponseParsers.parseDataModelsResponse(response);
    } catch (error) {
      console.error("Erro ao gerar modelos de dados:", error);
      throw new Error("Falha ao processar modelos com IA");
    }
  }

  async generateApiEndpoints(request: {
    projectDescription: string;
    objectives: string[];
    dataModels: any[];
    apiStyle: string;
    authRequired: boolean;
    technicalSolution?: any;
    stories?: any;
    timeline?: any;
  }): Promise<any[]> {
    if (!request.projectDescription?.trim()) {
      throw new Error("Descrição do projeto é obrigatória");
    }

    if (!request.objectives || request.objectives.length === 0) {
      throw new Error("Objetivos do projeto são obrigatórios");
    }

    if (!request.dataModels || request.dataModels.length === 0) {
      throw new Error("Modelos de dados são obrigatórios para gerar endpoints");
    }

    // Tentar geração completa primeiro, depois fallback para batches se necessário
    try {
      return await this.generateApiEndpointsComplete(request);
    } catch (error) {
      console.warn("⚠️ Geração completa falhou, tentando geração em batches...");
      return await this.generateApiEndpointsInBatches(request);
    }
  }

  private async generateApiEndpointsComplete(request: {
    projectDescription: string;
    objectives: string[];
    dataModels: any[];
    apiStyle: string;
    authRequired: boolean;
    technicalSolution?: any;
    stories?: any;
    timeline?: any;
  }): Promise<any[]> {
    const prompt = PromptBuilders.buildApiEndpointsPrompt(
      request.projectDescription,
      request.objectives,
      request.dataModels,
      request.apiStyle,
      request.authRequired,
      request.technicalSolution,
      request.stories,
      request.timeline
    );

    const response = await this.callGeminiAPI(prompt);

    // Log detalhado da resposta
    this.logApiResponse(response, 'generateApiEndpointsComplete', request);

    return ResponseParsers.parseApiEndpointsResponse(response);
  }

  private async generateApiEndpointsInBatches(request: {
    projectDescription: string;
    objectives: string[];
    dataModels: any[];
    apiStyle: string;
    authRequired: boolean;
    technicalSolution?: any;
    stories?: any;
    timeline?: any;
  }): Promise<any[]> {
    
    const allEndpoints: any[] = [];
    const batchSize = Math.max(1, Math.floor(request.dataModels.length / 3)); // Dividir em até 3 batches
    
    for (let i = 0; i < request.dataModels.length; i += batchSize) {
      const batch = request.dataModels.slice(i, i + batchSize);
      const batchNumber = Math.floor(i / batchSize) + 1;
      const totalBatches = Math.ceil(request.dataModels.length / batchSize);
      
      
      try {
        const batchPrompt = PromptBuilders.buildApiEndpointsPrompt(
          request.projectDescription,
          request.objectives,
          batch,
          request.apiStyle,
          request.authRequired,
          request.technicalSolution,
          request.stories,
          request.timeline
        );
        
        const response = await this.callGeminiAPI(batchPrompt);
        this.logApiResponse(response, `generateApiEndpointsBatch${batchNumber}`, { 
          ...request, 
          dataModelsCount: batch.length 
        });
        
        const batchEndpoints = ResponseParsers.parseApiEndpointsResponse(response);
        allEndpoints.push(...batchEndpoints);
        
        
        // Pequena pausa entre batches para evitar rate limiting
        if (i + batchSize < request.dataModels.length) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } catch (error) {
        console.error(`❌ Erro no batch ${batchNumber}:`, error);
        // Continua com os próximos batches mesmo se um falhar
      }
    }
    
    return allEndpoints;
  }

  private logApiResponse(response: string, method: string, request: any): void {
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      method,
      request: {
        projectDescription: request.projectDescription?.substring(0, 100) + '...',
        objectivesCount: request.objectives?.length,
        dataModelsCount: request.dataModelsCount || request.dataModels?.length,
        apiStyle: request.apiStyle,
        authRequired: request.authRequired
      },
      responseLength: response.length,
      responsePreview: response.substring(0, 500) + (response.length > 500 ? '...' : '')
    };
    
    console.group(`🤖 Gemini API Response - ${method} - ${timestamp}`);
    console.groupEnd();
  }
}
